import React, { useEffect, useState, useMemo } from 'react';
import { Tabs, Tab } from 'react-bootstrap';
import styled from 'styled-components';
import LeadListTriggerSection from './LeadListTriggerSection';
import LeadConditionBlock, { LeadCondition } from './LeadConditionBlock';
import TeamMemberItem from './teamMemberItem';
import { LeadListTriggerData } from './LeadListTriggerTypes';
import { IOrganizationEmployee, IOrganizationRole, IOrganizationTeam } from '../../utils/types';
import logger, { Logger } from '../../utils/logger';
import { useSelector } from 'react-redux';
import { Search } from 'lucide-react';

interface Props {
  mode: string;
  id?: string;
  isLoading: boolean;
  triggerData: LeadListTriggerData;
  setTriggerData: (data: LeadListTriggerData) => void;
  checkedState: {
    [_id: string]: { recepient: boolean; access: boolean };
  };
  setCheckedState: React.Dispatch<React.SetStateAction<{
    [_id: string]: { recepient: boolean; access: boolean };
  }>>;
  enableLeadDistribution: boolean;
  setEnableLeadDistribution: (enable: boolean) => void;
  distributionType: string;
  setDistributionType: (type: string) => void;
  conditions: LeadCondition[];
  setConditions: React.Dispatch<React.SetStateAction<LeadCondition[]>>;
}

// Styled Components
const StyledCheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  
  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    accent-color: #0d6efd;
    cursor: pointer;
  }
  
  label {
    font-weight: 500;
    cursor: pointer;
    margin: 0;
    color: #333;
  }
`;

const StyledSection = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
`;

const StyledSectionHeader = styled.div`
  margin-bottom: 16px;
  
  h6 {
    margin-bottom: 8px;
    font-weight: 600;
    color: #333;
  }
  
  p {
    color: #6c757d;
    margin-bottom: 8px;
    line-height: 1.5;
  }
  
  a {
    color: #0d6efd;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const StyledTeamMembersList = styled.div`
  margin-top: 16px;
  max-height: 45vh;
  overflow-y: auto;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;

    &:hover {
      background: #9ca3af;
    }
  }

  .form-group2 {
    margin-bottom: 12px;
    background: white;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 16px;
    transition: all 0.2s ease;
    
    &:hover {
      border-color: #dee2e6;
      box-shadow: 0 2px 4px rgba(0,0,0,0.05);
    }
  }
  
  .d-flex {
    display: flex !important;
  }
  
  .justify-content-between {
    justify-content: space-between !important;
  }
  
  .align-items-center {
    align-items: center !important;
  }
  
  .team-member-info {
    flex: 1;
    margin-right: 16px;
  }
  
  .team-member-name {
    color: #333 !important;
    font-weight: 500 !important;
    font-size: 14px !important;
    margin-bottom: 4px;
    line-height: 1.4;
    /* NO TRUNCATION - FULL NAME DISPLAY */
    white-space: normal !important;
    overflow: visible !important;
    text-overflow: initial !important;
    word-wrap: break-word;
  }
  
  .team-member-role {
    color: #6c757d !important;
    font-size: 12px !important;
    line-height: 1.3;
    /* NO TRUNCATION - FULL ROLE DISPLAY */
    white-space: normal !important;
    overflow: visible !important;
    text-overflow: initial !important;
    word-wrap: break-word;
  }
  
  /* Fix checkbox styling */
  input[type="checkbox"] {
    width: 18px !important;
    height: 18px !important;
    accent-color: #0d6efd !important;
    cursor: pointer !important;
    margin: 0 !important;
    background-color: white !important;
    border: 1px solid #dee2e6 !important;
    border-radius: 3px !important;
    flex-shrink: 0;
    
    &:checked {
      background-color: #0d6efd !important;
      border-color: #0d6efd !important;
    }
  }
`;

const StyledEmptyState = styled.div`
  text-align: center;
  padding: 32px 0;
  
  i {
    font-size: 24px;
    color: #6c757d;
    margin-bottom: 12px;
    display: block;
  }
  
  p {
    color: #6c757d;
    margin: 0;
  }
`;

const StyledFormGroup = styled.div`
  margin-bottom: 16px;
  
  label {
    display: block;
    margin-bottom: 6px;
    font-weight: 500;
    color: #333;
  }
  
  select {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    background-color: white;
    color: #333;
    
    &:focus {
      outline: none;
      border-color: #0d6efd;
      box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.25);
    }
  }
`;

const StyledConditionBlock = styled.div`
  margin-top: 40px;
  padding-bottom: 16px;
`;

const StyledSearchBar = styled.div`
  position: relative;
  margin-bottom: 12px;

  .search-icon {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: #9ca3af;
    pointer-events: none;
  }

  input {
    width: 100%;
    padding: 8px 12px 8px 34px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    font-size: 13px;
    color: #333;
    background: #f9fafb;
    transition: all 0.2s ease;

    &:focus {
      outline: none;
      border-color: #0d6efd;
      background: white;
      box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.15);
    }

    &::placeholder {
      color: #9ca3af;
    }
  }
`;



const TabContent: React.FC<Props> = ({
  mode,
  id,
  isLoading,
  triggerData,
  setTriggerData,
  checkedState,
  setCheckedState,
  enableLeadDistribution,
  setEnableLeadDistribution,
  distributionType,
  setDistributionType,
  conditions,
  setConditions
}) => {
  const [activeTab, setActiveTab] = useState('automation');
  const [memberSearchQuery, setMemberSearchQuery] = useState('');

  // ✅ ADD THIS - Redux store access like QuickFilters
  const StoreData = useSelector((state: any) => state?.rootReducers);
  const teamMembersList = StoreData?.userData?.userDetails?.organizationEmployee || [];
  const organizationTeams = StoreData?.userData?.userDetails?.organizationTeams || [];
  const organizationRoles = StoreData?.userData?.userDetails?.organizationRoles || [];
  const organizationName = StoreData?.userData?.userDetails?.organization?.name || '';

  // ✅ ADD THIS - Debug logging
  logger.log('TabContent - Redux data loaded:', {
    teamMembersCount: teamMembersList.length,
    organizationTeamsCount: organizationTeams.length,
    organizationRolesCount: organizationRoles.length,
    hasStoreData: !!StoreData,
    hasUserData: !!StoreData?.userData,
    hasUserDetails: !!StoreData?.userData?.userDetails
  });

  // Console logs for debugging (existing code stays)
  logger.log('TabContent - Props received:', {
    mode,
    id,
    isLoading,
    triggerData,
    conditionsLength: conditions.length,
    enableLeadDistribution,
    distributionType,
    checkedStateKeys: Object.keys(checkedState),
    activeTab
  });

  // Console logs for debugging
  logger.log('TabContent - Props received:', {
    mode,
    id,
    isLoading,
    triggerData,
    conditionsLength: conditions.length,
    enableLeadDistribution,
    distributionType,
    checkedStateKeys: Object.keys(checkedState),
    activeTab
  });

 const getActiveTeamMembers = () => {
  const activeMembers = teamMembersList?.filter((m: any) => m.isActive) || [];
  logger.log('TabContent - Active team members:', {
    totalMembers: teamMembersList.length,
    activeMembers: activeMembers.length,
    activeMembersList: activeMembers.map((m: any) => ({ 
      id: m._id, 
      name: `${m.firstName} ${m.lastName}`, 
      isActive: m.isActive 
    }))
  });
  return activeMembers;
};

  const handleChange = (type: "recepient" | "access", id: string) => {
    logger.log('TabContent - handleChange called:', { type, id, currentState: checkedState[id] });
    
    const updatedCheckedState = {
      ...checkedState[id],
      [type]: !checkedState[id]?.[type],
    };
    
    const newCheckedState = { ...checkedState, [id]: updatedCheckedState };
    logger.log('TabContent - Setting new checked state:', newCheckedState);
    
    setCheckedState(newCheckedState);
  };

  useEffect(() => {
    logger.log('TabContent - Props update effect:', {
      triggerData,
      conditionsLength: conditions.length,
      isVisible: !!triggerData.event,
      enableLeadDistribution
    });
  }, [triggerData, conditions, enableLeadDistribution]);

  // Remove the access members check effect - not needed anymore
  
  const renderEmptyState = (message: string) => (
    <StyledEmptyState>
      <i className="bi bi-people"></i>
      <p>{message}</p>
    </StyledEmptyState>
  );

const renderTeamMembers = (type: "recepient" | "access") => {
  const activeMembers = getActiveTeamMembers();
  
  logger.log('TabContent - renderTeamMembers called:', {
    type,
    activeMembersCount: activeMembers.length,
    checkedState,
    totalTeamMembers: teamMembersList.length,
    reduxDataLoaded: !!StoreData
  });
  
  if (activeMembers.length === 0) {
    logger.log('TabContent - No active members found:', {
      totalMembers: teamMembersList.length,
      allMembers: teamMembersList.map((m: any) => ({ 
        id: m._id, 
        name: `${m.firstName} ${m.lastName}`, 
        isActive: m.isActive 
      })),
      filterType: type
    });
    
    return renderEmptyState(
      type === "recepient" 
        ? "No team members available for lead distribution"
        : "No team members available to grant access"
    );
  }

  // Filter members by search query
  const filteredMembers = memberSearchQuery.trim()
    ? activeMembers.filter((member: any) => {
        const fullName = member.lastName
          ? `${member.firstName} ${member.lastName}`
          : member.firstName;
        return fullName.toLowerCase().includes(memberSearchQuery.toLowerCase());
      })
    : activeMembers;

  return (
    <>
      {/* Show search bar only when more than 10 team members */}
      {activeMembers.length > 10 && (
        <StyledSearchBar>
          <Search size={14} className="search-icon" />
          <input
            type="text"
            placeholder="Search team members..."
            value={memberSearchQuery}
            onChange={(e) => setMemberSearchQuery(e.target.value)}
          />
        </StyledSearchBar>
      )}
      {filteredMembers.map((member: any, i: number) => {
        const teamName = member.team
          ? organizationTeams.find((t: any) => t._id === member.team)?.name ?? organizationName
          : organizationName;
        const organizationRole = organizationRoles.find(
          (r: any) => r._id === member.role
        );

        const teamMember = {
          name: member.lastName
            ? `${member.firstName} ${member.lastName}`
            : member.firstName,
          organization: teamName,
          _id: member._id,
          role: organizationRole?.displayName ?? "",
        };

        const isChecked = checkedState[member._id]?.[type] || false;

        logger.log('TabContent - Rendering team member:', {
          memberName: teamMember.name,
          memberId: member._id,
          type,
          isChecked,
          checkedStateForMember: checkedState[member._id]
        });

        return (
          <div className="form-group2" key={i} style={{ marginBottom: '12px' }}>
            <div className="d-flex justify-content-between align-items-center">
              <div className="team-member-info">
                <div className="team-member-name" title={teamMember.name}>
                  {teamMember.name}
                </div>
                <div className="team-member-role">
                  {teamMember.organization}{teamMember.role ? ` - ${teamMember.role}` : ''}
                </div>
              </div>
              <input
                type="checkbox"
                checked={isChecked}
                onChange={() => handleChange(type, member._id)}
              />
            </div>
          </div>
        );
      })}
    </>
  );
};

  logger.log('TabContent - Render conditions check:', {
    event: triggerData.event,
    shouldShowConditions: ['create_lead', 'status_change', 'label_change', 'edit_lead'].includes(triggerData.event) && triggerData.event !== '',
    conditionsCount: conditions.length
  });

  return (
    <Tabs
      activeKey={activeTab}
      onSelect={(k) => {
        logger.log('TabContent - Tab changed to:', k);
        k && setActiveTab(k);
        setMemberSearchQuery('');
      }}
      className="mb-3"
    >
      {/* Automation Tab */}
      <Tab eventKey="automation" title="Automation">
        <StyledSection>
          <StyledSectionHeader>
            <h6>When This Happens...</h6>
            <p>
              Effortlessly keep your lead lists organized! Set up conditions like
              <strong> "status label updates" </strong> or 
              <strong> "lead source matches," </strong> and leads will automatically 
              move to this list when they meet your criteria.  
            </p>
            <p>
              For example, you can ensure all new Indiamart leads are instantly added 
              here without any manual effort.
            </p>
            <a
              href="https://help.3sigmacrm.com/welcome/managing-leads/custom-lead-list"
              target="_blank"
              rel="noopener noreferrer"
            >
              Learn how to set it up
            </a>
          </StyledSectionHeader>
          
          {/* Trigger Section */}
          <LeadListTriggerSection
            onChange={(data) => {
              logger.log('TabContent - Trigger data changed:', data);
              setTriggerData(data);

              // Clear conditions when trigger is cleared
              if (!data.event) {
                logger.log('TabContent - Clearing conditions because trigger was cleared');
                setConditions([]);
              }
            }}
            initialData={triggerData}
            isLoading={isLoading}
          />
          
          {/* Condition Block */}
          <StyledConditionBlock>
            <LeadConditionBlock
              onChange={(newConditions) => {
                logger.log('TabContent - Conditions changed:', newConditions);
                setConditions(newConditions);
              }}
              initialConditions={conditions}
              isVisible={['create_lead', 'status_change', 'label_change', 'edit_lead'].includes(triggerData.event) && triggerData.event !== ''}
              isLoading={isLoading}
            />
          </StyledConditionBlock>
        </StyledSection>
      </Tab>

      {/* Distribution Tab */}
      <Tab eventKey="distribution" title="Distribution">
        <StyledSection>
          <StyledSectionHeader>
            <h6>Lead Distribution</h6>
            <p>
              Enabling this means all leads added to this list will be automatically distributed among team members.
            </p>
            <p>
              Learn more about <a 
                href="https://help.3sigmacrm.com/welcome/managing-leads/custom-lead-list#id-1.-round-robin-lead-distribution"
                target="_blank"
                rel="noopener noreferrer"
              >
                round-robin lead distribution
              </a>.
            </p>
          </StyledSectionHeader>

          <StyledCheckboxContainer>
            <input
              type="checkbox"
              checked={enableLeadDistribution}
              onChange={(e) => {
                logger.log('TabContent - Distribution toggle changed:', e.target.checked);
                setEnableLeadDistribution(e.target.checked);
              }}
              id="enableDistribution"
            />
            <label htmlFor="enableDistribution">
              Enable lead distribution
            </label>
          </StyledCheckboxContainer>

          {enableLeadDistribution && (
            <div>
              <StyledFormGroup>
                <label>Distribution Type</label>
                <select
                  value={distributionType}
                  onChange={(e) => {
                    logger.log('TabContent - Distribution type changed:', e.target.value);
                    setDistributionType(e.target.value);
                  }}
                >
                  <option value="round-robin">Round Robin</option>
                </select>
              </StyledFormGroup>
              
              <StyledTeamMembersList>
                {renderTeamMembers("recepient")}
              </StyledTeamMembersList>
            </div>
          )}
        </StyledSection>
      </Tab>

      {/* Access Tab */}
      <Tab eventKey="access" title="Access">
        <StyledSection>
          <StyledSectionHeader>
            <h6>List Access</h6>
            <p>
              Grant access to all leads in this list to specific team members. This is useful when you want all users 
              to have shared access to the leads in this list. For example, teams with shared sales responsibilities, 
              multiple team leaders, or admins.
            </p>
            <p>
              Learn more about <a 
                href="https://help.3sigmacrm.com/welcome/managing-leads/custom-lead-list#managing-lead-access"
                target="_blank"
                rel="noopener noreferrer"
              >
                managing lead access
              </a>.
            </p>
          </StyledSectionHeader>

          <StyledTeamMembersList>
            {renderTeamMembers("access")}
          </StyledTeamMembersList>
        </StyledSection>
      </Tab>
    </Tabs>
  );
};

export default TabContent;